/**
 * First-person walkthrough scene for the warehouse layout.
 * Uses PointerLockControls for mouse look and WASD/arrow keys for movement.
 * Reuses Rack3D and WarehouseFloor from Layout3DScene.
 */

import React, { useRef, useMemo, useState, useCallback, useEffect } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import {
  PointerLockControls,
  KeyboardControls,
  useKeyboardControls,
  Environment,
  Text,
} from '@react-three/drei';
import * as THREE from 'three';
import { useLayoutEditor } from '../contexts/LayoutEditorContext';
import { Rack3D, WarehouseFloor } from './Layout3DScene';
import {
  RackDimensions,
} from '../types/warehouseLayout';
import { getRotatedDimensions } from '../utils/positionCalculator';

// ============================================
// Keyboard control mapping
// ============================================

enum Controls {
  forward = 'forward',
  backward = 'backward',
  left = 'left',
  right = 'right',
  sprint = 'sprint',
}

const keyboardMap = [
  { name: Controls.forward, keys: ['KeyW', 'ArrowUp'] },
  { name: Controls.backward, keys: ['KeyS', 'ArrowDown'] },
  { name: Controls.left, keys: ['KeyA', 'ArrowLeft'] },
  { name: Controls.right, keys: ['KeyD', 'ArrowRight'] },
  { name: Controls.sprint, keys: ['ShiftLeft', 'ShiftRight'] },
];

// ============================================
// Constants
// ============================================

const EYE_HEIGHT = 1.7;       // meters
const WALK_SPEED = 4.0;       // m/s
const SPRINT_SPEED = 8.0;     // m/s
const COLLISION_MARGIN = 0.3;  // meters from rack edge

// ============================================
// Player Movement Component (runs inside Canvas)
// ============================================

interface PlayerMovementProps {
  warehouseLength: number;
  warehouseWidth: number;
  rackColliders: Array<{ cx: number; cz: number; hw: number; hd: number }>;
  onLookedAtRack: (rackName: string | null) => void;
}

const PlayerMovement: React.FC<PlayerMovementProps> = ({
  warehouseLength,
  warehouseWidth,
  rackColliders,
  onLookedAtRack,
}) => {
  const { camera, scene } = useThree();
  const [, getKeys] = useKeyboardControls<Controls>();
  const raycaster = useRef(new THREE.Raycaster());
  const direction = useRef(new THREE.Vector3());
  const frontVector = useRef(new THREE.Vector3());
  const sideVector = useRef(new THREE.Vector3());
  const lastRaycastTime = useRef(0);

  useFrame((_state, delta) => {
    const { forward, backward, left, right, sprint } = getKeys();

    // Clamp delta to avoid huge jumps on tab-switch
    const dt = Math.min(delta, 0.1);
    const speed = sprint ? SPRINT_SPEED : WALK_SPEED;

    // Build movement vector relative to camera direction
    frontVector.current.set(0, 0, (backward ? 1 : 0) - (forward ? 1 : 0));
    sideVector.current.set((left ? 1 : 0) - (right ? 1 : 0), 0, 0);

    direction.current
      .subVectors(frontVector.current, sideVector.current)
      .normalize()
      .multiplyScalar(speed * dt)
      .applyEuler(camera.rotation);

    // Zero out vertical movement (stay on floor)
    direction.current.y = 0;

    // Proposed new position
    let newX = camera.position.x + direction.current.x;
    let newZ = camera.position.z + direction.current.z;

    // Collision detection against rack bounding boxes
    for (const collider of rackColliders) {
      const dx = newX - collider.cx;
      const dz = newZ - collider.cz;
      const overlapX = (collider.hw + COLLISION_MARGIN) - Math.abs(dx);
      const overlapZ = (collider.hd + COLLISION_MARGIN) - Math.abs(dz);

      if (overlapX > 0 && overlapZ > 0) {
        // Push out along the axis with smallest overlap
        if (overlapX < overlapZ) {
          newX = collider.cx + (dx > 0 ? 1 : -1) * (collider.hw + COLLISION_MARGIN);
        } else {
          newZ = collider.cz + (dz > 0 ? 1 : -1) * (collider.hd + COLLISION_MARGIN);
        }
      }
    }

    // Clamp to warehouse bounds
    newX = Math.max(0.5, Math.min(warehouseLength - 0.5, newX));
    newZ = Math.max(0.5, Math.min(warehouseWidth - 0.5, newZ));

    camera.position.x = newX;
    camera.position.z = newZ;
    camera.position.y = EYE_HEIGHT;

    // Raycast for rack name tooltip (throttle to ~10 Hz)
    const now = _state.clock.elapsedTime;
    if (now - lastRaycastTime.current > 0.1) {
      lastRaycastTime.current = now;
      raycaster.current.setFromCamera(new THREE.Vector2(0, 0), camera);
      raycaster.current.far = 15;
      const intersects = raycaster.current.intersectObjects(scene.children, true);

      let foundRack: string | null = null;
      for (const hit of intersects) {
        // Walk up parent chain to find a group with userData.rackNumber
        let obj: THREE.Object3D | null = hit.object;
        while (obj) {
          if (obj.userData?.rackNumber) {
            foundRack = obj.userData.rackNumber;
            break;
          }
          obj = obj.parent;
        }
        if (foundRack) break;
      }
      onLookedAtRack(foundRack);
    }
  });

  return null;
};

// ============================================
// Rack wrapper that sets userData for raycast
// ============================================

interface FPRackProps {
  position: { rackNumber: string; x: number; y: number; rotation: 0 | 90 | 180 | 270; label?: string };
  dimensions: RackDimensions;
}

const FPRack: React.FC<FPRackProps> = ({ position, dimensions }) => {
  const groupRef = useRef<THREE.Group>(null);

  useEffect(() => {
    if (groupRef.current) {
      // Tag the group and all children with rackNumber for raycast identification
      groupRef.current.userData = { rackNumber: position.label || position.rackNumber };
      groupRef.current.traverse((child) => {
        child.userData = { rackNumber: position.label || position.rackNumber };
      });
    }
  }, [position.rackNumber, position.label]);

  return (
    <group ref={groupRef}>
      <Rack3D
        position={position}
        dimensions={dimensions}
        isSelected={false}
        onClick={() => {}}
        showHeatmap={false}
        showActivity={false}
      />
    </group>
  );
};

// ============================================
// Main First Person Scene
// ============================================

const FirstPersonScene: React.FC = () => {
  const { state, rackDimensionsMap } = useLayoutEditor();
  const { layout } = state;
  const [isLocked, setIsLocked] = useState(false);
  const [lookedAtRack, setLookedAtRack] = useState<string | null>(null);

  const handleLookedAtRack = useCallback((name: string | null) => {
    setLookedAtRack(name);
  }, []);

  // Pre-compute collision boxes for all racks
  const rackColliders = useMemo(() => {
    if (!layout) return [];
    return layout.racks.map(rack => {
      const dim = rackDimensionsMap.get(rack.rackNumber) || {
        width: 6.8, depth: 1.3, height: 2.7, verticals: 10, shelves: 5,
      };
      const rotated = getRotatedDimensions(dim, rack.rotation);
      return {
        cx: rack.x,
        cz: rack.y,
        hw: rotated.width / 2,
        hd: rotated.depth / 2,
      };
    });
  }, [layout, rackDimensionsMap]);

  if (!layout) return null;

  const { dimensions, racks } = layout;

  // Start position: center of warehouse, facing forward
  const startPosition: [number, number, number] = [
    dimensions.length / 2,
    EYE_HEIGHT,
    dimensions.width - 2,
  ];

  return (
    <div id="fp-canvas-wrapper" className="w-full h-full bg-gray-900 rounded-lg overflow-hidden relative">
      <KeyboardControls map={keyboardMap}>
        <Canvas shadows>
          <perspectiveCamera
            position={startPosition}
            fov={75}
          />

          <ambientLight intensity={0.5} />
          <directionalLight
            position={[dimensions.length / 2, 12, dimensions.width / 2]}
            intensity={0.8}
            castShadow
            shadow-mapSize={[2048, 2048]}
          />
          <pointLight position={[dimensions.length * 0.25, 6, dimensions.width * 0.5]} intensity={0.3} />
          <pointLight position={[dimensions.length * 0.75, 6, dimensions.width * 0.5]} intensity={0.3} />

          <Environment preset="warehouse" />

          {/* Ceiling for immersion */}
          <mesh rotation={[Math.PI / 2, 0, 0]} position={[dimensions.length / 2, dimensions.height, dimensions.width / 2]}>
            <planeGeometry args={[dimensions.length, dimensions.width]} />
            <meshStandardMaterial color="#1f2937" side={THREE.DoubleSide} />
          </mesh>

          <WarehouseFloor length={dimensions.length} width={dimensions.width} />

          {racks.map(rack => {
            const dim = rackDimensionsMap.get(rack.rackNumber) || {
              width: 6.8, depth: 1.3, height: 2.7, verticals: 10, shelves: 5,
            };
            return (
              <FPRack
                key={rack.rackNumber}
                position={rack}
                dimensions={dim}
              />
            );
          })}

          {/* Dimension labels on floor */}
          <Text position={[dimensions.length / 2, 0.05, -0.5]} fontSize={0.6} color="#6b7280" anchorX="center" rotation={[-Math.PI / 2, 0, 0]}>
            {dimensions.length}m
          </Text>
          <Text position={[-0.5, 0.05, dimensions.width / 2]} fontSize={0.6} color="#6b7280" anchorX="center" rotation={[-Math.PI / 2, 0, Math.PI / 2]}>
            {dimensions.width}m
          </Text>

          <PointerLockControls
            selector="#fp-canvas-wrapper"
            onLock={() => setIsLocked(true)}
            onUnlock={() => setIsLocked(false)}
          />

          <PlayerMovement
            warehouseLength={dimensions.length}
            warehouseWidth={dimensions.width}
            rackColliders={rackColliders}
            onLookedAtRack={handleLookedAtRack}
          />
        </Canvas>
      </KeyboardControls>

      {/* Crosshair */}
      {isLocked && (
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
          <div className="w-6 h-6 relative">
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white/50 -translate-x-1/2" />
            <div className="absolute top-1/2 left-0 right-0 h-px bg-white/50 -translate-y-1/2" />
          </div>
        </div>
      )}

      {/* Rack tooltip */}
      {isLocked && lookedAtRack && (
        <div className="absolute top-1/2 left-1/2 translate-x-4 -translate-y-8 pointer-events-none">
          <div className="bg-black/80 text-white text-sm px-3 py-1.5 rounded-lg whitespace-nowrap">
            {lookedAtRack}
          </div>
        </div>
      )}

      {/* Instructions overlay */}
      {!isLocked && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/40 pointer-events-none">
          <div className="bg-gray-900/90 border border-gray-700 rounded-xl px-8 py-6 text-center max-w-md pointer-events-auto">
            <div className="text-4xl mb-3">🚶</div>
            <h3 className="text-white text-lg font-semibold mb-2">Førsteperson-modus</h3>
            <p className="text-gray-300 text-sm mb-4">
              Klikk for å gå inn i lageret
            </p>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm text-left">
              <div className="text-gray-400">WASD / Piltaster</div>
              <div className="text-white">Gå rundt</div>
              <div className="text-gray-400">Mus</div>
              <div className="text-white">Se deg rundt</div>
              <div className="text-gray-400">Shift</div>
              <div className="text-white">Sprint</div>
              <div className="text-gray-400">ESC</div>
              <div className="text-white">Stopp</div>
            </div>
          </div>
        </div>
      )}

      {/* HUD when locked */}
      {isLocked && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 pointer-events-none">
          <div className="bg-black/60 text-gray-400 text-xs px-4 py-2 rounded-full">
            WASD for å gå · Shift for å løpe · ESC for å stoppe
          </div>
        </div>
      )}
    </div>
  );
};

export default FirstPersonScene;
